import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import ProgressStepper from './ProgressStepper';

const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

function ScreeningSubmit() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const tenantPays = searchParams.get('pay') === 'true';
  const paidReturn = searchParams.get('paid') === 'true';
  const [screeningInfo, setScreeningInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [idVerified, setIdVerified] = useState(false);
  const [idVerifying, setIdVerifying] = useState(false);
  const [currentStep, setCurrentStep] = useState('personal');
  const stepPanelRef = useRef(null);
  const [form, setForm] = useState({
    full_name: '', date_of_birth: '', current_address: '',
    employer: '', job_title: '', monthly_income: '', years_employed: '',
    previous_landlord_name: '', previous_landlord_phone: '',
    credit_check_consent: false,
    consent_signature: '',
  });

  useEffect(() => {
    const fetchInfo = async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/screening/submit/${token}`);
        const data = await res.json();
        if (!res.ok) throw new Error(data.error || 'Invalid or expired screening link');
        setScreeningInfo(data);

        const idRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/screening/submit/${token}/identity/status`);
        const idData = await idRes.json();
        if (idData.status === 'verified') setIdVerified(true);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchInfo();
  }, [token]);

  const handleVerifyIdentity = async () => {
    setIdVerifying(true);
    setError(null);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/screening/submit/${token}/identity/start`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to start identity verification');

      const stripe = await loadStripe(STRIPE_PUBLISHABLE_KEY);
      const { error: stripeError } = await stripe.verifyIdentity(data.client_secret);

      if (stripeError) {
        setError('Identity verification was cancelled. Please try again.');
        setIdVerifying(false);
        return;
      }

      let attempts = 0;
      const poll = setInterval(async () => {
        attempts++;
        try {
          const statusRes = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/screening/submit/${token}/identity/status`);
          const statusData = await statusRes.json();
          if (statusData.status === 'verified') {
            clearInterval(poll);
            setIdVerified(true);
            setIdVerifying(false);
          } else if (statusData.status === 'canceled' || attempts > 30) {
            clearInterval(poll);
            setError('Identity verification failed. Please try again.');
            setIdVerifying(false);
          }
        } catch { /* retry */ }
      }, 2000);
    } catch (err) {
      setError(err.message);
      setIdVerifying(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm({ ...form, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.credit_check_consent) {
      setError('You must consent to the credit check to proceed.');
      return;
    }
    if (!form.consent_signature.trim()) {
      setError('Please type your full name as your electronic signature.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/screening/submit/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, tenant_pays: tenantPays }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Submission failed');
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else {
        setSubmitted(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  /* ── Step definitions ── */
  const steps = useMemo(() => [
    { key: 'personal', title: 'Personal Info', description: 'Your name, date of birth, and current address.' },
    { key: 'employment', title: 'Employment & References', description: 'Employment details and previous landlord reference.' },
    { key: 'identity', title: 'Identity Verification', description: 'Verify your identity with a government ID and selfie.' },
    { key: 'consent', title: 'Consent & Submit', description: 'Authorize a soft credit check and submit your application.' },
  ], []);

  const stepKeys = steps.map(s => s.key);
  const stepIndex = stepKeys.indexOf(currentStep);

  const isStepComplete = useCallback((key) => {
    switch (key) {
      case 'personal':
        return !!(form.full_name.trim() && form.date_of_birth);
      case 'employment':
        return !!(form.employer.trim() || form.job_title.trim() || form.monthly_income);
      case 'identity':
        return idVerified;
      case 'consent':
        return !!(form.credit_check_consent && form.consent_signature.trim());
      default:
        return false;
    }
  }, [form, idVerified]);

  const canAdvance = useCallback((key) => {
    switch (key) {
      case 'personal':
        return !!(form.full_name.trim() && form.date_of_birth);
      case 'employment':
        return true; // optional fields
      case 'identity':
        return idVerified;
      case 'consent':
        return false; // last step
      default:
        return false;
    }
  }, [form, idVerified]);

  const stepperSteps = useMemo(() => steps.map(step => ({
    key: step.key,
    label: step.title,
    status: isStepComplete(step.key) ? 'approved' : 'pending',
  })), [steps, isStepComplete]);

  const stepperProgress = useMemo(() => {
    const completed = stepperSteps.filter(s => s.status === 'approved').length;
    return completed / stepperSteps.length;
  }, [stepperSteps]);

  const goToStep = (key) => {
    setCurrentStep(key);
    setError(null);
    window.setTimeout(() => {
      stepPanelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  };

  const nextStep = steps[stepIndex + 1];
  const prevStep = steps[stepIndex - 1];

  if (loading) {
    return <main className="App-main"><p style={{ textAlign: 'center', marginTop: '3rem' }}>Loading...</p></main>;
  }

  if (error && !screeningInfo) {
    return (
      <main className="App-main">
        <div style={{ maxWidth: 480, margin: '3rem auto', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>⚠️</div>
          <h2>Unable to load screening</h2>
          <p className="status-text">{error}</p>
        </div>
      </main>
    );
  }

  if (submitted || paidReturn) {
    return (
      <main className="App-main">
        <div style={{ maxWidth: 480, margin: '3rem auto', textAlign: 'center' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>✅</div>
          <h2>{paidReturn ? 'Payment received' : 'Application submitted'}</h2>
          <p className="status-text" style={{ lineHeight: 1.6 }}>
            Thank you! {paidReturn ? 'Your payment has been received and your' : 'Your'} information has been submitted. A soft credit check will be performed — this will not affect your credit score.
            <br /><br />
            Your landlord will receive the screening report shortly.
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="App-main document-upload-page">
      <div style={{ maxWidth: 720, margin: '2rem auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ marginBottom: '0.35rem' }}>Tenant Screening Application</h2>
          <p className="status-text">
            {screeningInfo.landlord_name} has requested a screening for{' '}
            <strong>{screeningInfo.rental_address}{screeningInfo.rental_unit ? `, Unit ${screeningInfo.rental_unit}` : ''}</strong>.
          </p>
        </div>

        <ProgressStepper
          steps={stepperSteps}
          currentKey={currentStep}
          onSelect={goToStep}
          progress={stepperProgress}
        />

        <div className="review-wizard" style={{ marginTop: '1.5rem' }}>
          {/* ── Sidebar ── */}
          <aside className="review-steps">
            <div className="section-header" style={{ justifyContent: 'flex-start', textAlign: 'left' }}>
              <h3>Steps</h3>
            </div>
            <p className="status-text" style={{ marginTop: '-0.25rem', marginBottom: '0.75rem' }}>
              Complete each section to submit your screening.
            </p>
            {steps.map((step) => {
              const done = isStepComplete(step.key);
              const chipClass = done ? 'report-chip--success' : 'report-chip--muted';
              const statusLabel = done ? 'Complete' : 'Pending';
              return (
                <button
                  key={step.key}
                  type="button"
                  className={`review-step ${currentStep === step.key ? 'active' : ''}`}
                  onClick={() => goToStep(step.key)}
                >
                  <div className="review-step-header">
                    <span className="review-step-title">{step.title}</span>
                    <span className={`report-chip ${chipClass}`}>{statusLabel}</span>
                  </div>
                  <p className="status-text" style={{ marginTop: '0.35rem' }}>{step.description}</p>
                </button>
              );
            })}
          </aside>

          {/* ── Main panel ── */}
          <div className="review-main" ref={stepPanelRef}>
            <div key={currentStep} className="review-panel">
              <form onSubmit={handleSubmit}>
                {error && <p className="error-message">{error}</p>}

                {/* ── Step 1: Personal Info ── */}
                {currentStep === 'personal' && (
                  <div className="dossier-section">
                    <div className="section-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Personal Info</h3>
                      <span className="report-chip report-chip--info">Step 1 of {steps.length}</span>
                    </div>
                    <p className="status-text" style={{ marginTop: '0.35rem' }}>Tell us who you are.</p>

                    <div className="form-group" style={{ marginTop: '1rem' }}>
                      <label htmlFor="full_name">Full legal name *</label>
                      <input type="text" id="full_name" name="full_name" value={form.full_name} onChange={handleChange} required placeholder="John Michael Smith" />
                    </div>

                    <div className="form-group">
                      <label htmlFor="date_of_birth">Date of birth *</label>
                      <input type="date" id="date_of_birth" name="date_of_birth" value={form.date_of_birth} onChange={handleChange} required />
                    </div>

                    <div className="form-group">
                      <label htmlFor="current_address">Current address</label>
                      <input type="text" id="current_address" name="current_address" value={form.current_address} onChange={handleChange} placeholder="456 Oak Ave, Toronto, ON M5V 2K1" />
                    </div>
                  </div>
                )}

                {/* ── Step 2: Employment & References ── */}
                {currentStep === 'employment' && (
                  <div className="dossier-section">
                    <div className="section-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Employment & References</h3>
                      <span className="report-chip report-chip--info">Step 2 of {steps.length}</span>
                    </div>
                    <p className="status-text" style={{ marginTop: '0.35rem' }}>Provide your employment details and previous landlord info if available.</p>

                    <div className="form-group" style={{ marginTop: '1rem' }}>
                      <label htmlFor="employer">Employer name</label>
                      <input type="text" id="employer" name="employer" value={form.employer} onChange={handleChange} placeholder="Acme Corp" />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="job_title">Job title</label>
                        <input type="text" id="job_title" name="job_title" value={form.job_title} onChange={handleChange} placeholder="Software Developer" />
                      </div>
                      <div className="form-group">
                        <label htmlFor="monthly_income">Monthly income (gross)</label>
                        <input type="number" id="monthly_income" name="monthly_income" value={form.monthly_income} onChange={handleChange} placeholder="5000" min="0" step="0.01" />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="years_employed">Years at current job</label>
                      <input type="text" id="years_employed" name="years_employed" value={form.years_employed} onChange={handleChange} placeholder="3 years" />
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                      <div className="form-group">
                        <label htmlFor="previous_landlord_name">Previous landlord name</label>
                        <input type="text" id="previous_landlord_name" name="previous_landlord_name" value={form.previous_landlord_name} onChange={handleChange} />
                      </div>
                      <div className="form-group">
                        <label htmlFor="previous_landlord_phone">Previous landlord phone</label>
                        <input type="tel" id="previous_landlord_phone" name="previous_landlord_phone" value={form.previous_landlord_phone} onChange={handleChange} />
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Step 3: Identity Verification ── */}
                {currentStep === 'identity' && (
                  <div className="dossier-section">
                    <div className="section-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Identity Verification</h3>
                      <span className="report-chip report-chip--info">Step 3 of {steps.length}</span>
                    </div>
                    <p className="status-text" style={{ marginTop: '0.35rem' }}>Verify your identity with a government-issued ID and a quick selfie.</p>

                    <div className="dashboard-section" style={{
                      marginTop: '1rem', padding: '1.25rem',
                      borderLeft: `4px solid ${idVerified ? '#16a34a' : '#2563eb'}`
                    }}>
                      {idVerified ? (
                        <div>
                          <p style={{ margin: 0, fontWeight: 500, color: '#16a34a' }}>✅ Identity verified</p>
                          <p className="status-text" style={{ marginTop: '0.25rem' }}>
                            Your identity has been confirmed. You can proceed to the next step.
                          </p>
                        </div>
                      ) : (
                        <div>
                          <p className="status-text" style={{ margin: '0 0 0.75rem' }}>
                            For your security, we need to verify your identity before running a credit check.
                            You'll be asked to take a photo of your government-issued ID and a quick selfie.
                          </p>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                            <button type="button" className="primary-button" onClick={handleVerifyIdentity} disabled={idVerifying}
                              style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}>
                              {idVerifying ? 'Verifying...' : 'Verify my identity'}
                            </button>
                            <span className="camera-trouble-wrap">
                              <button
                                type="button"
                                className="status-pill warning"
                                style={{ fontSize: '0.75rem', cursor: 'pointer', border: 'none' }}
                                onClick={(e) => { e.stopPropagation(); const el = e.currentTarget.nextElementSibling; el.classList.toggle('camera-hint-visible'); }}
                              >
                                Camera trouble?
                              </button>
                              <div className="camera-hint-box">
                                <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>Camera not working?</p>
                                <p style={{ margin: '0 0 0.5rem' }}>
                                  <strong>On mobile:</strong> Make sure you're using Safari (iPhone) or Chrome (Android) — not an in-app browser. Copy the link and paste it into your browser.
                                </p>
                                <p style={{ margin: '0 0 0.5rem' }}>
                                  <strong>On desktop:</strong> Allow camera access when prompted, or try a different browser.
                                </p>
                                <button
                                  type="button"
                                  className="secondary-button"
                                  style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem' }}
                                  onClick={() => { navigator.clipboard.writeText(window.location.href).then(() => alert('Link copied!')).catch(() => {}); }}
                                >
                                  Copy this page link
                                </button>
                              </div>
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* ── Step 4: Consent & Submit ── */}
                {currentStep === 'consent' && (
                  <div className="dossier-section">
                    <div className="section-header" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                      <h3>Consent & Submit</h3>
                      <span className="report-chip report-chip--info">Step 4 of {steps.length}</span>
                    </div>
                    <p className="status-text" style={{ marginTop: '0.35rem' }}>Authorize a soft credit check and submit your application.</p>

                    <div style={{
                      marginTop: '1rem', padding: '1.25rem', borderRadius: '10px',
                      background: 'var(--surface-color, var(--secondary-color, #f9fafb))',
                      border: '1px solid var(--border-color, #e5e7eb)',
                      opacity: idVerified ? 1 : 0.5,
                      pointerEvents: idVerified ? 'auto' : 'none'
                    }}>
                      <p style={{ fontWeight: 600, fontSize: '0.9rem', margin: '0 0 0.75rem' }}>Credit Check Consent</p>
                      <p style={{ fontSize: '0.82rem', lineHeight: 1.6, margin: '0 0 1rem', color: 'var(--light-text-color)' }}>
                        I, {form.full_name || '[your name]'}, hereby authorize Rentual Corp. and its designated agents to obtain a consumer credit report
                        from TransUnion Canada for the purpose of evaluating my rental application for the property located at{' '}
                        <strong>{screeningInfo.rental_address}{screeningInfo.rental_unit ? `, Unit ${screeningInfo.rental_unit}` : ''}</strong>.
                        I understand this is a <strong>soft inquiry</strong> and will not affect my credit score. This consent is valid for this screening request only.
                      </p>
                      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', cursor: 'pointer', marginBottom: '1rem' }}>
                        <input type="checkbox" name="credit_check_consent" checked={form.credit_check_consent} onChange={handleChange}
                          style={{ marginTop: '0.25rem', width: '18px', height: '18px', flexShrink: 0 }} />
                        <span style={{ fontSize: '0.85rem', lineHeight: 1.55 }}>
                          I have read and agree to the above consent.
                        </span>
                      </label>
                      {form.credit_check_consent && (
                        <div className="form-group" style={{ marginBottom: 0 }}>
                          <label htmlFor="consent_signature" style={{ fontSize: '0.82rem' }}>Type your full name as signature *</label>
                          <input type="text" id="consent_signature" name="consent_signature" value={form.consent_signature} onChange={handleChange}
                            required placeholder="John Michael Smith"
                            style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic', fontSize: '1rem' }} />
                        </div>
                      )}
                    </div>

                    {!idVerified && (
                      <div style={{
                        marginTop: '0.75rem', padding: '0.75rem 1rem', borderRadius: '10px',
                        background: 'rgba(234,179,8,0.08)', border: '1px solid rgba(234,179,8,0.3)',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text-color)' }}>
                          ⚠️ Please complete identity verification (Step 3) before submitting.
                        </p>
                      </div>
                    )}

                    {tenantPays && (
                      <div style={{
                        marginTop: '0.75rem', padding: '0.75rem 1rem', borderRadius: '10px',
                        background: 'rgba(40,182,200,0.06)', border: '1px solid var(--accent-color, #28b6c8)',
                        textAlign: 'center'
                      }}>
                        <p style={{ margin: 0, fontSize: '0.85rem' }}>
                          💳 A $29 screening fee will be charged after you submit. You'll be redirected to a secure payment page.
                        </p>
                      </div>
                    )}

                    <button type="submit" className="primary-button"
                      disabled={submitting || !idVerified || !form.credit_check_consent || !form.consent_signature.trim()}
                      style={{ width: '100%', marginTop: '1.25rem', justifyContent: 'center' }}>
                      {submitting ? 'Submitting...' : !idVerified ? 'Complete identity verification first' : tenantPays ? 'Continue to payment — $29' : 'Submit application'}
                    </button>

                    <p className="status-text" style={{ textAlign: 'center', marginTop: '0.75rem', fontSize: '0.78rem' }}>
                      Your information is encrypted and only shared with your landlord. Powered by Rentual.
                    </p>
                  </div>
                )}

                {/* ── Step navigation ── */}
                <div className="form-actions" style={{ justifyContent: 'space-between', marginTop: '1.5rem' }}>
                  <button
                    type="button"
                    className="secondary-button"
                    disabled={!prevStep}
                    onClick={() => prevStep && goToStep(prevStep.key)}
                  >
                    Back
                  </button>
                  {nextStep && (
                    <button
                      type="button"
                      className="primary-button"
                      disabled={currentStep === 'personal' && !canAdvance('personal')}
                      onClick={() => goToStep(nextStep.key)}
                    >
                      Next: {nextStep.title}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}

export default ScreeningSubmit;
